/* ===================================================================
   Automations -- PIXEL-FAITHFUL rebuild of the Claude Design mockup
   ("Rhody Reviews.dc.html", AUTOMATIONS screen). Corbato, 2026-07-03.

   Reproduces the mockup EXACTLY (desktop lines 1090-1164, mobile lines
   2358-2427): a master on/off switch, four automation rules -- three with
   an editable timing stepper (- N +) + unit pill + channel pill that cycles
   SMS/Email, and a "draft thank-you" rule -- and the reassurance banner.

   ONE responsive component. Both the desktop and mobile subtrees are in the
   DOM; CSS (styles/automations.css) shows exactly one at the 1024px switch
   (>=1024 desktop, <1024 mobile; 768-1023 is "enhanced mobile" per HANDOFF
   section 8 and uses the mobile subtree). No bespoke tablet DOM.

   ---- HARD PRODUCT RULE (critical, preserved verbatim in the banner) ----
   Automations ONLY SEND REVIEW REQUESTS. They NEVER post replies and NEVER
   auto-post anything. Every reply to a Google review is drafted for the
   owner to approve on the Reviews screen. Customers marked do-not-contact
   are always skipped, and sends respect quiet hours in the customer's TZ.
   (Hard Product Rule #1.) The "Draft a thank-you when a review lands" rule
   only DRAFTS a reply for approval -- it does not post.

   ---- DATA WIRING PRESERVED ----
   The list + master switch stay wired to the real hooks (useAutomations /
   useUpdateAutomation). Each of the four named rules binds to a live
   automation matched by trigger code when the backend supplies one; its
   toggle persists the `enabled` flag via PUT and the list refetches. The
   master switch reflects "any rule active" and flips every wired rule at once.

   ---- CADENCE IS READ-ONLY (2026-07-06 launch QC) ----
   The automations API exposes NO per-rule timing/channel field, so the old
   interactive timing stepper + unit pill + channel pill silently discarded any
   change (only the on/off toggle persisted). We never ship a fully interactive
   control that throws away input: the timing/channel are now shown as plain,
   non-editable text with a "cadence is set for you" caption. Owners still
   toggle each rule on/off (that persists).
   =================================================================== */
import { useMemo, useState } from 'react';
import { useAutomations, useUpdateAutomation, useCreateAutomation } from '../hooks';
import type { Automation } from '../lib/api';
import '../styles/automations.css';

/* ------------------------------------------------------------------ */
/* Local primitives (not in ui.tsx): the mockup's exact track/knob     */
/* toggle. Flagged as a LOCAL build per the coordination rule.         */
/* ------------------------------------------------------------------ */
function AuToggle({
  on,
  onClick,
  label,
}: {
  on: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      className={'au-track' + (on ? ' is-on' : '')}
      onClick={onClick}
      role="switch"
      aria-checked={on}
      aria-label={label}
    >
      <span className="au-knob" />
    </button>
  );
}

/* ---- inline icons, lifted verbatim from the mockup SVG paths -------- */
function BoltIcon({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#1a73e8" strokeWidth={2}>
      <path d="M13 2L4 14h6l-1 8 9-12h-6z" />
    </svg>
  );
}
const RULE_ICON_STROKE = '#5f6368';
function CheckMarkIcon() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke={RULE_ICON_STROKE} strokeWidth={2}>
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}
function ClockIcon() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke={RULE_ICON_STROKE} strokeWidth={2}>
      <path d="M12 8v5l3 2" />
      <circle cx="12" cy="12" r="9" />
    </svg>
  );
}
function SeasonIcon() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke={RULE_ICON_STROKE} strokeWidth={2}>
      <path d="M12 2v4M12 18v4M4 12H2M22 12h-2M6 6L4 4M20 20l-2-2M6 18l-2 2M20 4l-2 2" />
      <circle cx="12" cy="12" r="4" />
    </svg>
  );
}
function HeartIcon() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke={RULE_ICON_STROKE} strokeWidth={2}>
      <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1 1.1L12 21l7.8-7.5 1-1.1a5.5 5.5 0 0 0 0-7.8z" />
    </svg>
  );
}
function ShieldCheckIcon({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#1a73e8" strokeWidth={2}>
      <path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* Fixed, READ-ONLY cadence per rule (the API has no timing/channel     */
/* field; these are shown as plain text, never as editable controls).   */
/* Email is the live channel; the cadence phrasing stays channel-        */
/* neutral so it is honest regardless.                                   */
/* ------------------------------------------------------------------ */
type TimingKey = 'afterJob' | 'reminder' | 'seasonal';

const FIXED_TIMING: Record<TimingKey, string> = {
  afterJob: 'about an hour',
  reminder: '3 days',
  seasonal: '90 days',
};
const DEFAULT_RULES = {
  afterJob: true,
  reminder: true,
  seasonal: false,
  thankReviewer: true,
} as const;
type RuleKey = keyof typeof DEFAULT_RULES;

/* Trigger code -> named rule mapping, so the four fixed rules bind to real
   automations when the backend supplies them. Presentation is fixed. */
const TRIGGER_FOR_RULE: Record<RuleKey, string> = {
  afterJob: 'job_completed',
  reminder: 'no_review_after_3d',
  seasonal: 'quarterly_winback',
  thankReviewer: 'new_5star',
};

/* Descriptive action string written when a rule has no server row yet (new
   tenants start with zero automations). Convention matches the demo seed. */
const DEFAULT_ACTION: Record<RuleKey, string> = {
  afterJob: 'Send a review request after a job is marked complete.',
  reminder: 'Send one gentle reminder if no review after 3 days.',
  seasonal: 'Send a seasonal check-in about every 90 days.',
  thankReviewer: 'Draft a thank-you reply when a new 5-star review lands.',
};

export function Automations() {
  const q = useAutomations();
  const updater = useUpdateAutomation();
  const creator = useCreateAutomation();
  const serverRules = useMemo<Automation[]>(() => q.data?.automations ?? [], [q.data]);

  /* Live automation (if any) backing each named rule. */
  const ruleAutomation = useMemo(() => {
    const map = {} as Record<RuleKey, Automation | undefined>;
    (Object.keys(TRIGGER_FOR_RULE) as RuleKey[]).forEach((k) => {
      map[k] = serverRules.find((r) => String(r.trigger) === TRIGGER_FOR_RULE[k]);
    });
    return map;
  }, [serverRules]);

  /* Local mirror of enabled flags (seeded from server when present, else the
     mockup defaults). Optimistic; the refetch reconciles on server writes. */
  const [localRules, setLocalRules] = useState<Record<RuleKey, boolean>>({ ...DEFAULT_RULES });
  const [master, setMaster] = useState(true);

  const ruleOn = (k: RuleKey): boolean => {
    const a = ruleAutomation[k];
    return a ? Boolean(a.enabled) : localRules[k];
  };

  /* Persist a rule's enabled flag. When the tenant already has a server row,
     PUT it; otherwise CREATE one (new tenants start with zero rows, so a plain
     no-op silently discarded every toggle). Both mutations invalidate the
     automations query, so ruleAutomation reflects the new row on refetch.
     Failures surface via updater.error / creator.error (see saveError). */
  function persist(k: RuleKey, next: boolean) {
    const a = ruleAutomation[k];
    if (a) {
      void updater.update(a.id, { enabled: next }).catch(() => {
        /* refetch restores true state on failure; saveError shows the message */
      });
    } else {
      void creator
        .create({ trigger: TRIGGER_FOR_RULE[k], action: DEFAULT_ACTION[k], enabled: next })
        .catch(() => {
          /* refetch restores true state on failure; saveError shows the message */
        });
    }
  }

  function toggleRule(k: RuleKey) {
    const next = !ruleOn(k);
    setLocalRules((s) => ({ ...s, [k]: next }));
    persist(k, next);
  }

  const activeRuleCount = (Object.keys(DEFAULT_RULES) as RuleKey[]).filter(ruleOn).length;

  function toggleMaster() {
    const next = !master;
    setMaster(next);
    setLocalRules((s) => {
      const upd = { ...s };
      (Object.keys(DEFAULT_RULES) as RuleKey[]).forEach((k) => {
        upd[k] = next;
        persist(k, next);
      });
      return upd;
    });
  }

  const masterLabel = master ? 'On' : 'Paused';
  const masterColor = master ? '#188038' : '#5f6368';

  /* A create or update that failed. Shown inline near the rule list so a
     toggle that didn't save is never silent. */
  const saveError = updater.error ?? creator.error;

  return (
    <>
      {/* ============================ DESKTOP ============================ */}
      <div className="au-desktop">
        <div className="au-d-head">
          <div>
            <h1>Automations</h1>
            <p>Set the rules once. Rhody asks for reviews at the right moment, and you stay in control.</p>
          </div>
        </div>

        <div className="au-d-body">
          {/* MASTER SWITCH */}
          <div className="au-d-master">
            <div className="au-mico">
              <BoltIcon size={26} />
            </div>
            <div className="au-mbody">
              <div className="au-mtitle-row">
                <span className="au-mtitle">Automatic review requests</span>
                <span className="au-status" style={{ color: masterColor }}>
                  <span className="au-dot" />
                  {masterLabel}
                </span>
              </div>
              <div className="au-msub">
                {activeRuleCount} of 4 rules active. Requests send by SMS or email using your templates.
              </div>
            </div>
            <AuToggle on={master} onClick={toggleMaster} label="Automatic review requests" />
          </div>

          {/* RULES */}
          <div className="au-d-rules">
            <div className="au-d-rules-head">Rules</div>
            <div className="au-d-cadence-note">
              Turn each rule on or off. The timing is set for you for the best response.
            </div>

            {saveError && (
              <div
                role="alert"
                style={{ fontSize: 13, color: 'var(--danger, #C0392B)', marginTop: 8 }}
              >
                Couldn&rsquo;t save that change. Please try again.
              </div>
            )}

            {/* Rule 1: after completed job */}
            <div className="au-d-rule">
              <div className="au-rico">
                <CheckMarkIcon />
              </div>
              <div className="au-rmain">
                <div className="au-rtitle">Ask after a completed job</div>
                <div className="au-d-prose">
                  Send a review request <span className="au-d-fixed">{FIXED_TIMING.afterJob}</span> after the
                  job is marked done.
                </div>
              </div>
              <AuToggle on={ruleOn('afterJob')} onClick={() => toggleRule('afterJob')} label="Ask after a completed job" />
            </div>

            {/* Rule 2: one reminder */}
            <div className="au-d-rule">
              <div className="au-rico">
                <ClockIcon />
              </div>
              <div className="au-rmain">
                <div className="au-rtitle">Send one reminder</div>
                <div className="au-d-prose">
                  If there&rsquo;s still no review, wait <span className="au-d-fixed">{FIXED_TIMING.reminder}</span>,
                  then send one gentle nudge. Never more than one.
                </div>
              </div>
              <AuToggle on={ruleOn('reminder')} onClick={() => toggleRule('reminder')} label="Send one reminder" />
            </div>

            {/* Rule 3: seasonal check-in */}
            <div className="au-d-rule">
              <div className="au-rico">
                <SeasonIcon />
              </div>
              <div className="au-rmain">
                <div className="au-rtitle">Seasonal check-in</div>
                <div className="au-d-prose">
                  Re-ask past customers who never reviewed every{' '}
                  <span className="au-d-fixed">{FIXED_TIMING.seasonal}</span>.
                </div>
              </div>
              <AuToggle on={ruleOn('seasonal')} onClick={() => toggleRule('seasonal')} label="Seasonal check-in" />
            </div>

            {/* Rule 4: draft thank-you (never posts) */}
            <div className="au-d-rule">
              <div className="au-rico">
                <HeartIcon />
              </div>
              <div className="au-rmain">
                <div className="au-rtitle au-rtitle--tight">Draft a thank-you when a review lands</div>
                <div className="au-d-subnote">
                  AI writes a reply in your voice and drops it in your inbox. <strong>You still approve every post.</strong>
                </div>
              </div>
              <AuToggle
                on={ruleOn('thankReviewer')}
                onClick={() => toggleRule('thankReviewer')}
                label="Draft a thank-you when a review lands"
              />
            </div>
          </div>

          {/* REASSURANCE (HARD RULE copy -- preserved verbatim) */}
          <div className="au-d-reassure">
            <ShieldCheckIcon size={20} />
            <div className="au-rtxt">
              <strong>Automations only send requests. They never post replies for you.</strong> Every reply to
              a Google review is drafted for you to approve. Customers marked do-not-contact are always skipped.
            </div>
          </div>
        </div>
      </div>

      {/* ============================ MOBILE ============================= */}
      <div className="au-mobile">
        <div className="au-m-body">
          <div>
            <div className="au-m-title">Automations</div>
            <div className="au-m-sub">Set the rules once &middot; you stay in control</div>
          </div>

          {/* master (mobile) */}
          <div className="au-m-master">
            <div className="au-mico">
              <BoltIcon size={22} />
            </div>
            <div className="au-mbody">
              <div className="au-mtitle">Auto requests</div>
              <div className="au-msub" style={{ color: masterColor }}>
                {masterLabel} &middot; {activeRuleCount} of 4 rules
              </div>
            </div>
            <AuToggle on={master} onClick={toggleMaster} label="Automatic review requests" />
          </div>

          {saveError && (
            <div
              role="alert"
              style={{ fontSize: 13, color: 'var(--danger, #C0392B)' }}
            >
              Couldn&rsquo;t save that change. Please try again.
            </div>
          )}

          {/* rules (mobile) */}
          <div className="au-m-rules">
            {/* Rule 1 */}
            <div className="au-m-rule">
              <div className="au-m-rule-top">
                <div className="au-mr-main">
                  <div className="au-m-rtitle">Ask after a completed job</div>
                  <div className="au-m-rdesc">Once a job is marked done</div>
                </div>
                <AuToggle on={ruleOn('afterJob')} onClick={() => toggleRule('afterJob')} label="Ask after a completed job" />
              </div>
              <div className="au-m-timing au-m-timing--fixed">
                <span className="au-m-label">AFTER</span>
                <span className="au-m-fixed">{FIXED_TIMING.afterJob}</span>
              </div>
            </div>

            {/* Rule 2 */}
            <div className="au-m-rule">
              <div className="au-m-rule-top">
                <div className="au-mr-main">
                  <div className="au-m-rtitle">Send one reminder</div>
                  <div className="au-m-rdesc">Only once</div>
                </div>
                <AuToggle on={ruleOn('reminder')} onClick={() => toggleRule('reminder')} label="Send one reminder" />
              </div>
              <div className="au-m-timing au-m-timing--fixed">
                <span className="au-m-label">WAIT</span>
                <span className="au-m-fixed">{FIXED_TIMING.reminder}</span>
              </div>
            </div>

            {/* Rule 3 */}
            <div className="au-m-rule">
              <div className="au-m-rule-top">
                <div className="au-mr-main">
                  <div className="au-m-rtitle">Seasonal check-in</div>
                  <div className="au-m-rdesc">Recurring cadence</div>
                </div>
                <AuToggle on={ruleOn('seasonal')} onClick={() => toggleRule('seasonal')} label="Seasonal check-in" />
              </div>
              <div className="au-m-timing au-m-timing--fixed">
                <span className="au-m-label">EVERY</span>
                <span className="au-m-fixed">{FIXED_TIMING.seasonal}</span>
              </div>
            </div>

            {/* Rule 4 (draft thank-you, no timing row) */}
            <div className="au-m-rule au-m-rule--flat">
              <div className="au-mr-main">
                <div className="au-m-rtitle">Draft thank-you replies</div>
                <div className="au-m-rdesc">You still approve every post</div>
              </div>
              <AuToggle
                on={ruleOn('thankReviewer')}
                onClick={() => toggleRule('thankReviewer')}
                label="Draft thank-you replies"
              />
            </div>
          </div>

          {/* reassurance (mobile, HARD RULE copy) */}
          <div className="au-m-reassure">
            <ShieldCheckIcon size={18} />
            <div className="au-rtxt">
              <strong>Automations only send requests.</strong> They never post replies. You approve every one.
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
